'use strict';

import Products from './Products';
import ApplicationConstants from '../constants/ApplicationConstants';

const formKeys = ApplicationConstants.FormKeys;
const editable = () => false;
const readonly = () => true;
const isAdviser = () => !!(window.NOA && window.NOA.userContext && window.NOA.userContext.isAdviser);
const isInvestor = () => !!(window.NOA && window.NOA.userContext && window.NOA.userContext.isInvestor);
const isSupplied = (value) => !!(value && value.length > 0);
const isSuppliedAndAdviser = (value) => isSupplied(value) && isAdviser();
const isIndividual = (client) => client.customerType === ApplicationConstants.ClientTypes.INDIVIDUAL;
const isBusiness = (client) => client.customerType === ApplicationConstants.ClientTypes.BUSINESS;
const isSoleTrader = (accountType) => accountType === ApplicationConstants.AccountTypeCodes.SOLE_TRADER;
const isCustomTitle = (client) => client.title === ApplicationConstants.CustomTitle;
class ClientEditabilityRulesClass {
    constructor() {
    }

    _getNameRule(client, formKey) {
        switch (formKey) {
            case formKeys.COMPANY_INVESTMENT:
            case formKeys.PARTNERSHIP_INVESTMENT:
            case formKeys.SMSF_INVESTMENT:
            case formKeys.TRUST_INVESTMENT:
                if (isIndividual(client)) {
                    return isAdviser();
                }
                break;
            case formKeys.SOLE_TRADER_INVESTMENT:
                if (isBusiness(client)) {
                    return null;
                }
        }

        return readonly();
    }

    _getBusinessNameRule(client, formKey, isTrusteeCorporate) {
        switch (formKey) {
            case formKeys.SOLE_TRADER_INVESTMENT:
                return isIndividual(client) ? editable() : isAdviser();
            case formKeys.PARTNERSHIP_INVESTMENT:
                return isInvestor();
            case formKeys.COMPANY_INVESTMENT:
            case formKeys.COMPANY_CHALLENGER:
            case formKeys.COMPANY_COMMINSURE:
            case formKeys.SMSF_CHALLENGER:
            case formKeys.SMSF_COMMINSURE:
            case formKeys.TRUST_COMMINSURE:
            case formKeys.TRUST_CHALLENGER:
                return readonly();
            case formKeys.TRUST_INVESTMENT:
            case formKeys.SMSF_INVESTMENT:
                if (isTrusteeCorporate) {
                    return isAdviser();
                }
                else {
                    return editable();
                }
        }

        return editable();
    }

    _getBusinessAddressRule(formKey, isTrusteeCorporate) {
        switch (formKey) {
            case formKeys.SOLE_TRADER_INVESTMENT:
            case formKeys.COMPANY_INVESTMENT:
            case formKeys.PARTNERSHIP_INVESTMENT:
                return isAdviser();
            case formKeys.COMPANY_CHALLENGER:
            case formKeys.SMSF_CHALLENGER:
            case formKeys.TRUST_CHALLENGER:
            case formKeys.COMPANY_COMMINSURE:
            case formKeys.SMSF_COMMINSURE:
            case formKeys.TRUST_COMMINSURE:
                return readonly();
            case formKeys.TRUST_INVESTMENT:
            case formKeys.SMSF_INVESTMENT:
                if (isTrusteeCorporate) {
                    return isAdviser()
                }
                else {
                    return editable();
                }
        }

        return editable();
    }

    _getGenderRule(accountType, client) {
        return isSoleTrader(accountType) && isBusiness(client)? null : isSupplied(client.gender);
    }

    _getOccupationRule(accountType, client) {
        return isSoleTrader(accountType) && isBusiness(client)? null : isSuppliedAndAdviser(client.occupation);
    }

    _getTitleRule(client, formKey) {
        if (!isSupplied(client.title)) {
            return editable();
        }
        else if (isSupplied(client.title) && isCustomTitle(client)) {
            return readonly();
        }
        else if (isSupplied(client.title) && !isSupplied(client.gender)) {
            return editable();
        }
        else {
            switch (formKey) {
                case formKeys.COMPANY_INVESTMENT:
                case formKeys.PARTNERSHIP_INVESTMENT:
                case formKeys.SMSF_INVESTMENT:
                case formKeys.TRUST_INVESTMENT:
                    if (isIndividual(client)) {
                        return isAdviser();
                    }
                    break;
                case formKeys.SOLE_TRADER_INVESTMENT:
                    if (isBusiness(client)) {
                        return null;
                    }
            }
            return readonly();   
        }
    }

    getRules(accountType, productId, client, isTrusteeCorporate) {     
        const formKey = Products.getFormKey(accountType, productId);
        const businessAddressRule = this._getBusinessAddressRule(formKey, isTrusteeCorporate);
        const nameRule = this._getNameRule(client, formKey);
        const isAdviserRule = isAdviser();

        return {
            titleReadonly: this._getTitleRule(client, formKey),
            givenNameReadonly: nameRule,
            lastNameReadonly: nameRule,
            genderReadonly: this._getGenderRule(accountType, client),
            dateOfBirthReadonly: isSupplied(client.dateOfBirth),
            occupationReadonly: this._getOccupationRule(accountType, client),
            countryOfResidenceReadonly: isSuppliedAndAdviser(client.countryOfResidence),
            tfnNumberReadonly: isSupplied(client.tfnNumber),
            addressInfo: {
                addressLineReadonly:  Products.isAnnuity(productId) ? isAdviserRule : editable(),
                cityStatePostCodeReadonly: Products.isAnnuity(productId) ? isAdviserRule : editable(),
                resAusStateReadonly: Products.isAnnuity(productId) ? isAdviserRule : editable(),
                postCodeReadonly: Products.isAnnuity(productId) ? isAdviserRule : editable(),
                resCountryReadonly: Products.isAnnuity(productId) ? isAdviserRule : editable()
            },
            mobileNumberReadonly: isAdviserRule,
            homeNumberReadonly: isAdviserRule,
            workNumberReadonly: isAdviserRule,
            emailAddressReadonly: editable(),
            isAustralianResidentReadonly: isAdviserRule,
            isOtherCountryTaxResidentReadonly: null, 
            otherCountryTaxResidenceReadonly: null,
            isCitizenOfOtherCountryReadonly: isAdviserRule,
            citizenship1Readonly: isSupplied(client.citizenship1),
            citizenship2Readonly: isSupplied(client.citizenship1) && isSupplied(client.citizenship2),
            primaryBusinessActivityReadonly: isSuppliedAndAdviser(client.primaryBusinessActivity),
            countryEstablishedReadonly: isSuppliedAndAdviser(client.countryEstablished),
            businessNameReadonly: this._getBusinessNameRule(client, formKey, isTrusteeCorporate),
            businessAddress: {
                addressLineReadonly: businessAddressRule,
                cityStatePostCodeReadonly: businessAddressRule,
                resAusStateReadonly: businessAddressRule,
                postCodeReadonly: businessAddressRule,
                resCountryReadonly: businessAddressRule
            },
            abnReadonly: isSupplied(client.abn)
        };
    }

    getRulesForCfpAdvisedWithCommSeeKyc(productId) {
        return {
            titleReadonly: true,
            genderReadonly: true,
            givenNameReadonly: true,
            lastNameReadonly: true,
            dateOfBirthReadonly: true,
            addressInfo: {
                addressLineReadonly: Products.isAnnuity(productId) ? true : false,
                cityStatePostCodeReadonly: Products.isAnnuity(productId) ? true : false,
                resAusStateReadonly: Products.isAnnuity(productId) ? true : false,
                postCodeReadonly: Products.isAnnuity(productId) ? true : false,
                resCountryReadonly: Products.isAnnuity(productId) ? true : false
            }
        };
    }
}

const ClientEditabilityRules = new ClientEditabilityRulesClass();
export default ClientEditabilityRules;


// WEBPACK FOOTER //
// C:/GH/1106/advice.noa.origination/~/eslint-loader!./utils/ClientEditabilityRules.js